import {
    CommunityId,
    fullSingleLineAddress,
    HasAddress,
    ReferenceBusinessId,
} from "@sp-crm/core";
import {
    EntityDetails,
    EntityOrganization,
    labeledContactPhones,
    LabeledPhone,
    present,
} from "components/details-page-v-next/entity-contact-details";
import { AdvancedSearchEntityType } from "generated/graphql";

interface ContactEntityFields extends HasAddress {
    name: string | null;
    role: string | null;
    contactType: string | null;
    cellPhone: string | null;
    phone1: string | null;
    fax: string | null;
    email1: string | null;
    email1OptOut: boolean;
    website: string | null;
}

interface CommunityFields extends HasAddress {
    id: CommunityId;
    name: string | null;
    mainPhone: string | null;
    website: string | null;
    email: string | null;
}

interface ReferenceContactSource {
    contactEntity: ContactEntityFields | null;
    business: {
        id: ReferenceBusinessId;
        businessContactEntity: ContactEntityFields | null;
    } | null;
    community: CommunityFields | null;
}

interface ReferenceBusinessSource {
    email: string | null;
    businessContactEntity: ContactEntityFields | null;
}

const organizationName = (name: string | null | undefined): string =>
    present(name) ?? "(no name)";

const contactPhones = (contactEntity: ContactEntityFields | null): LabeledPhone[] =>
    labeledContactPhones({
        cell: contactEntity?.cellPhone ?? null,
        alternate: contactEntity?.phone1 ?? null,
    });

const singlePhone = (value: string | null | undefined): LabeledPhone[] => {
    const phone = present(value);

    return phone ? [{ label: null, value: phone }] : [];
};

const organizationAddress = (reference: ReferenceContactSource): HasAddress | null =>
    reference.business?.businessContactEntity ?? reference.community;

const addressLine = (address: HasAddress | null): string | null =>
    address ? present(fullSingleLineAddress(address)) : null;

const contactOrganization = (
    reference: ReferenceContactSource,
): EntityOrganization | null => {
    if (reference.business) {
        const entity = reference.business.businessContactEntity;

        return {
            name: organizationName(entity?.name),
            href: `/references/organizations/show/${reference.business.id}`,
            entityId: reference.business.id,
            entityType: AdvancedSearchEntityType.ReferenceBusiness,
            address: addressLine(entity),
            phone: present(entity?.phone1),
            website: present(entity?.website),
            email: present(entity?.email1),
        };
    }

    if (reference.community) {
        return {
            name: organizationName(reference.community.name),
            href: `/communities/show/${reference.community.id}`,
            entityId: reference.community.id,
            entityType: AdvancedSearchEntityType.Community,
            address: addressLine(reference.community),
            phone: present(reference.community.mainPhone),
            website: present(reference.community.website),
            email: present(reference.community.email),
        };
    }

    return null;
};

export const referenceContactDetails = (
    reference: ReferenceContactSource,
): EntityDetails => {
    const { contactEntity } = reference;

    return {
        classification: present(contactEntity?.role),
        classificationLabel: "Role",
        phones: contactPhones(contactEntity),
        fax: null,
        email: present(contactEntity?.email1),
        emailOptOut: contactEntity?.email1OptOut ?? false,
        address: addressLine(organizationAddress(reference)),
        website: null,
        organization: contactOrganization(reference),
    };
};

export const referenceBusinessDetails = (
    reference: ReferenceBusinessSource,
): EntityDetails => {
    const { businessContactEntity } = reference;

    return {
        classification: present(businessContactEntity?.contactType),
        classificationLabel: "Type",
        phones: singlePhone(businessContactEntity?.phone1),
        fax: present(businessContactEntity?.fax),
        email: present(reference.email),
        emailOptOut: false,
        address: addressLine(businessContactEntity),
        website: present(businessContactEntity?.website),
        organization: null,
    };
};

interface ReferenceContactOwnDetailsSource {
    contactEntity: ContactEntityFields | null;
    summary: string | null;
    answers: readonly unknown[];
}

export const hasReferenceContactDetails = (
    reference: ReferenceContactOwnDetailsSource,
): boolean => {
    const { contactEntity } = reference;

    return !!(
        present(contactEntity?.name) ||
        present(contactEntity?.role) ||
        contactPhones(contactEntity).length > 0 ||
        present(contactEntity?.email1) ||
        present(reference.summary) ||
        reference.answers.length > 0
    );
};
